import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { SELECT_CLS } from "@/lib/network";

const LIMIT = 200;

const W = { val: (w) => w.wilayah_id, label: (w) => w.nama_wilayah, sub: (w) => w.wilayah_id };

// Pilihan yang bisa diketik (default: wilayah); `allLabel` → tambahkan opsi kosong (filter "Semua ...").
export function WilayahCombobox({ options, value, onChange, testid, placeholder = "— Pilih —", allLabel, disabled, className = "",
                                  getValue = W.val, getLabel = W.label, getSub = W.sub,
                                  searchPlaceholder = "Cari nama atau kode wilayah…", emptyText = "Wilayah tidak ditemukan." }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sorted = useMemo(() => [...options].sort((a, b) => (getLabel(a) || "").localeCompare(getLabel(b) || "", "id", { numeric: true, sensitivity: "base" })), [options, getLabel]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? sorted.filter((w) => `${getLabel(w)} ${getSub(w)}`.toLowerCase().includes(s)) : sorted;
  }, [sorted, q, getLabel, getSub]);
  const cur = options.find((w) => getValue(w) === value);
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };

  return (
    <Popover modal open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid}
                className={`${SELECT_CLS} flex items-center justify-between gap-1 text-left disabled:cursor-not-allowed disabled:bg-[#F3F4F6] disabled:opacity-70 ${className}`}>
          <span className={`truncate ${cur ? "" : "text-[#6B7280]"}`}>{cur ? getLabel(cur) : (allLabel || placeholder)}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            {allLabel && !q && <CommandItem value="__all" onSelect={() => pick("")} data-testid={`${testid}-opt-all`}>
              <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />{allLabel}</CommandItem>}
            {matches.slice(0, LIMIT).map((w) => (
              <CommandItem key={getValue(w)} value={getValue(w)} onSelect={() => pick(getValue(w))} data-testid={`${testid}-opt-${getValue(w)}`}>
                <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${value === getValue(w) ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{getLabel(w)}</span>
                <span className="ml-auto whitespace-nowrap pl-2 font-mono-tab text-[10px] opacity-60">{getSub(w)}</span>
              </CommandItem>
            ))}
            {matches.length > LIMIT && <p className="px-3 py-2 text-[11px] text-[#6B7280]" data-testid={`${testid}-more`}>
              Menampilkan {LIMIT} dari {matches.length}. Ketik untuk mempersempit.</p>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
